import { useState, useEffect, useCallback, useRef } from 'react';
import { tradingService } from '../../../../services/tradingService';

const useStrategyData = () => {
  const [source, setSource] = useState('backtest'); // backtest | simulation
  const [sourceIds, setSourceIds] = useState(null);  // specific IDs or null for all
  const [strategyFilter, setStrategyFilter] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [rfRate, setRfRate] = useState(0.025);
  const [benchmark, setBenchmark] = useState('SPY');
  const [focus, setFocus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cache = useRef({});

  // Available strategies and backtests for filter dropdowns
  const [availableStrategies, setAvailableStrategies] = useState([]);

  const loadStrategies = useCallback(async () => {
    try {
      const res = await tradingService.getAvailableStrategies();
      setAvailableStrategies(res.data.strategies || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    loadStrategies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear cache when params change
  const paramsKey = JSON.stringify({
    source, sourceIds, strategyFilter, symbolFilter,
    date_start: dateRange.start, date_end: dateRange.end,
    rf_rate: rfRate, benchmark,
  });

  const prevParamsKey = useRef(paramsKey);
  useEffect(() => {
    if (prevParamsKey.current !== paramsKey) {
      cache.current = {};
      prevParamsKey.current = paramsKey;
    }
  }, [paramsKey]);

  const cachedFetch = useCallback(async (key, fetchFn) => {
    if (cache.current[key]) return cache.current[key];
    const result = await fetchFn();
    cache.current[key] = result;
    return result;
  }, []);

  const getAnalyticsParams = () => ({
    source,
    source_ids: sourceIds || undefined,
    strategy_filter: strategyFilter || undefined,
    symbol_filter: symbolFilter || undefined,
    date_start: dateRange.start || undefined,
    date_end: dateRange.end || undefined,
    rf_rate: rfRate,
    benchmark,
    focus: focus || undefined,
  });

  return {
    source, sourceIds, strategyFilter, symbolFilter, dateRange, rfRate, benchmark, focus,
    loading, error, availableStrategies,
    setSource, setSourceIds, setStrategyFilter, setSymbolFilter,
    setDateRange, setRfRate, setBenchmark, setFocus, setLoading, setError,
    getAnalyticsParams, cachedFetch, loadStrategies,
  };
};

export default useStrategyData;
